// @ts-nocheck

import React, { useCallback, useEffect, useRef, useState, type Node } from 'react';
import styled, { css } from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  overflow-y: hidden;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  overflow-y: auto;
  height: 100%;
  max-height: 100%;

  ${({ $noScrollbar }) =>
    $noScrollbar
      ? css`
          &::-webkit-scrollbar {
            display: none;
          }
        `
      : undefined}

  ::before,
  ::after {
    content: '';
    position: absolute;
    left: 0;
    z-index: 100;
    width: 100%;
    height: 1em;

    pointer-events: none;
  }

  ${({ $currentScroll, $isScrollable }) =>
    $isScrollable
      ? css`
          ::before {
            top: 0;
            background: radial-gradient(farthest-side at 50% 0%, rgba(0, 0, 0, 0.35), transparent);
            opacity: ${$currentScroll};
          }

          ::after {
            bottom: 0;
            background: radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.35), transparent);
            opacity: ${1 - $currentScroll};
          }
        `
      : null}
`;

type ScrollShadowProps = {
  className?: string;
  children?: Node;
  isReady?: boolean;
  noScrollbar?: boolean;
  anchorId?: string;
};

const ScrollShadow = React.forwardRef<ScrollShadowProps, Wrapper>(function ScrollShadow(
  { className, children, isReady, noScrollbar = false, anchorId }: ScrollShadowProps,
  forwardedRef,
) {
  const [currentScroll, setCurrentScroll] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const contentRef = useRef();

  const onContentScroll = useCallback((event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    setCurrentScroll(scrollTop / (scrollHeight - clientHeight));
  }, []);

  const checkIfScrollable = useCallback((node) => {
    if (node) {
      const { scrollHeight, clientHeight } = node;
      setIsScrollable(scrollHeight - clientHeight > 5);
    }
  }, []);

  useEffect(() => {
    if (isReady === undefined || isReady) {
      checkIfScrollable(contentRef.current);
    }
  }, [isReady, checkIfScrollable]);

  return (
    <Wrapper className={className}>
      <Content
        data-anchorid={anchorId}
        ref={(ref) => {
          if (forwardedRef) {
            // $FlowFixMe
            forwardedRef.current = ref;
          }
          contentRef.current = ref;
        }}
        $currentScroll={currentScroll}
        $isScrollable={isScrollable}
        onScroll={onContentScroll}
        $noScrollbar={noScrollbar}
      >
        {children}
      </Content>
    </Wrapper>
  );
});

export default ScrollShadow;
